const SERVICE_WORKER_URL = '/sw.js';
const UPDATE_THROTTLE_MS = 30_000;
const CONTROLLER_RELOAD_COOLDOWN_MS = 10_000;
const CONTROLLER_RELOAD_SESSION_KEY = 'inhound:pwa-controller-reload-at';

let loadListenerInstalled = false;
let recoveryListenersInstalled = false;
let controllerListenerInstalled = false;
let registrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;
let activeRegistration: ServiceWorkerRegistration | null = null;
let updateInFlight = false;
let lastRecoveryUpdateAt = 0;
let controllerChangeCanReload = false;
let controllerReloadedThisPage = false;

export const serviceWorkerRegistrationRuntime = {
  now: () => Date.now(),
  reload: () => window.location.reload(),
};

function getReloadCooldownTimestamp(): number {
  try {
    return Number(window.sessionStorage.getItem(CONTROLLER_RELOAD_SESSION_KEY) ?? '0');
  } catch {
    return 0;
  }
}

function markControllerReload(): void {
  try {
    window.sessionStorage.setItem(CONTROLLER_RELOAD_SESSION_KEY, String(serviceWorkerRegistrationRuntime.now()));
  } catch {
    // Storage access can be denied in hardened browser modes; the in-page guard still prevents loops.
  }
}

async function requestServiceWorkerUpdate(
  registration: ServiceWorkerRegistration | null,
  options: { force?: boolean } = {},
): Promise<void> {
  if (!registration || updateInFlight) return;

  const now = serviceWorkerRegistrationRuntime.now();
  if (!options.force && now - lastRecoveryUpdateAt < UPDATE_THROTTLE_MS) return;

  updateInFlight = true;
  if (!options.force) {
    lastRecoveryUpdateAt = now;
  }

  try {
    await registration.update();
  } catch (error) {
    console.warn('[pwa] service worker update check failed', error);
  } finally {
    updateInFlight = false;
  }
}

function installRecoveryUpdateListeners(): void {
  if (recoveryListenersInstalled) return;
  recoveryListenersInstalled = true;

  window.addEventListener('online', () => {
    void requestServiceWorkerUpdate(activeRegistration);
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void requestServiceWorkerUpdate(activeRegistration);
    }
  });
}

function installControllerChangeListener(hadControllerBeforeRegistration: boolean): void {
  controllerChangeCanReload = controllerChangeCanReload || hadControllerBeforeRegistration;

  if (controllerListenerInstalled) return;
  controllerListenerInstalled = true;

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!controllerChangeCanReload) {
      controllerChangeCanReload = true;
      return;
    }

    const lastReloadAt = getReloadCooldownTimestamp();
    const isWithinReloadCooldown =
      serviceWorkerRegistrationRuntime.now() - lastReloadAt < CONTROLLER_RELOAD_COOLDOWN_MS;
    if (controllerReloadedThisPage || isWithinReloadCooldown) {
      return;
    }

    controllerReloadedThisPage = true;
    markControllerReload();
    serviceWorkerRegistrationRuntime.reload();
  });
}

function startRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (registrationPromise) return registrationPromise;

  const hadControllerBeforeRegistration = Boolean(navigator.serviceWorker.controller);
  installControllerChangeListener(hadControllerBeforeRegistration);
  installRecoveryUpdateListeners();

  registrationPromise = navigator.serviceWorker
    .register(SERVICE_WORKER_URL, { updateViaCache: 'none' })
    .then((registration) => {
      activeRegistration = registration;
      void requestServiceWorkerUpdate(registration, { force: true });
      return registration;
    })
    .catch((error) => {
      console.warn('[pwa] service worker registration failed', error);
      return null;
    });

  return registrationPromise;
}

export function registerServiceWorker() {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;

  if (loadListenerInstalled) return;
  loadListenerInstalled = true;

  window.addEventListener('load', () => {
    void startRegistration();
  });
}

export function resetServiceWorkerRegistrationStateForTests() {
  loadListenerInstalled = false;
  recoveryListenersInstalled = false;
  controllerListenerInstalled = false;
  registrationPromise = null;
  activeRegistration = null;
  updateInFlight = false;
  lastRecoveryUpdateAt = 0;
  controllerChangeCanReload = false;
  controllerReloadedThisPage = false;
}
